import React, { useMemo } from 'react';

import { useToastContext } from '@rtkwlf/fenrir-react';
import { captureException } from '@sentry/react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  keepPreviousData,
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import hasIn from 'lodash/hasIn';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import { parse, stringify } from 'query-string';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import { env } from '@packages/utils/env';

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

const handleThrowOnError = (error: unknown) => {
  // API error
  if (isObject(error) && hasIn(error, 'response.status')) {
    return false;
  }

  // not an API error
  captureException(error, { tags: { query: 'throwOnError' } });

  // throw the error in development for visibility
  return env.MODE === 'development' || env.MODE === 'test';
};

export const QueryProvider = ({ children }: React.PropsWithChildren) => {
  const { addToast } = useToastContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          mutations: {
            throwOnError: handleThrowOnError,
          },
          queries: {
            placeholderData: keepPreviousData,
            refetchOnMount: true,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: false,
            throwOnError: handleThrowOnError,
          },
        },
        queryCache: new QueryCache({
          onError: async (error, query) => {
            const { onErrorToastData } = query.meta ?? {};

            const toastData = isFunction(onErrorToastData)
              ? await onErrorToastData(error)
              : onErrorToastData;

            if (!toastData) return;

            addToast(toastData);
          },
        }),
        mutationCache: new MutationCache({
          onError: async (error, _variables, _context, mutation) => {
            const { onErrorToastData } = mutation.meta ?? {};

            const toastData = isFunction(onErrorToastData)
              ? await onErrorToastData(error)
              : onErrorToastData;

            if (!toastData) return;

            addToast(toastData);
          },
        }),
      }),
    [addToast],
  );

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        objectToSearchString: stringify,
        searchStringToObject: parse,
      }}
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              const { options } = query;

              return (
                query.state.status === 'success' &&
                Boolean(options.gcTime && options.gcTime > 0) &&
                query.meta?.persist !== false
              );
            },
          },
          persister: localStoragePersister,
        }}
      >
        {children}
        {env.MODE === 'development' && (
          <ReactQueryDevtools buttonPosition="bottom-left" />
        )}
      </PersistQueryClientProvider>
    </QueryParamProvider>
  );
};
